import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getEmployees, createEmployee, updateEmployee, deleteEmployee, getLocations, addEmployeeLocation, removeEmployeeLocation, getEmployee } from '../api';

function Employees() {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    base_pay_rate: '',
    seniority_level: 1,
    pin_code: ''
  });
  const [locationFormData, setLocationFormData] = useState({
    location_id: '',
    template_rank: ''
  });
  // Get tenant_id from logged-in user
const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user.tenant_id || 1;

  const fetchEmployees = async () => {
    try {
      const response = await getEmployees(tenantId);
      setEmployees(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load employees');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await getLocations(tenantId);
      setLocations(response.data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const fetchEmployeeDetails = async (id) => {
    try {
      const response = await getEmployee(id);
      setSelectedEmployee(response.data);
    } catch (err) {
      console.error('Failed to load employee details:', err);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchLocations();
  }, []);

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      base_pay_rate: '',
      seniority_level: 1,
      pin_code: ''
    });
    setEditingEmployee(null);
    setShowForm(false);
  };

  const handleEdit = (employee) => {
    setFormData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      phone: employee.phone || '',
      email: employee.email || '',
      base_pay_rate: employee.base_pay_rate || '',
      seniority_level: employee.seniority_level || 1,
      pin_code: employee.pin_code || ''
    });
    setEditingEmployee(employee);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        tenant_id: tenantId,
        base_pay_rate: parseFloat(formData.base_pay_rate) || 0,
        seniority_level: parseInt(formData.seniority_level) || 1
      };

      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, data);
      } else {
        await createEmployee(data);
      }
      
      fetchEmployees();
      resetForm();
    } catch (err) {
      alert('Failed to save employee');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this employee?')) {
      try {
        await deleteEmployee(id);
        fetchEmployees();
        if (selectedEmployee?.id === id) {
          setSelectedEmployee(null);
        }
      } catch (err) {
        alert('Failed to deactivate employee');
        console.error(err);
      }
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    
    try {
      await addEmployeeLocation(selectedEmployee.id, {
        location_id: parseInt(locationFormData.location_id),
        template_rank: parseInt(locationFormData.template_rank) || 999
      });
      fetchEmployeeDetails(selectedEmployee.id);
      setLocationFormData({ location_id: '', template_rank: '' });
      setShowLocationForm(false);
    } catch (err) {
      alert('Failed to add location');
      console.error(err);
    }
  };

  const handleRemoveLocation = async (locationId) => {
    if (!selectedEmployee) return;
    
    try {
      await removeEmployeeLocation(selectedEmployee.id, locationId);
      fetchEmployeeDetails(selectedEmployee.id);
    } catch (err) {
      alert('Failed to remove location');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Loading employees...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto pr-4 pl-0 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <Link to="/" className="text-blue-500 hover:text-blue-700 mb-2 inline-block">&larr; Back to Home</Link>
            <h1 className="text-3xl font-bold text-gray-900">Employees</h1>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Add Employee
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Employee Form Modal */}
        {showForm && (
          <div className="fixed inset-0 flex items-center justify-center z-50" style={{backgroundColor: 'rgba(0, 0, 0, 0.75)'}}>
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" style={{backgroundColor: '#ffffff', opacity: 1}}>
              <h2 className="text-xl font-semibold mb-4">
                {editingEmployee ? 'Edit Employee' : 'Add Employee'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Base Pay Rate ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.base_pay_rate}
                        onChange={(e) => setFormData({ ...formData, base_pay_rate: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Seniority Level</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.seniority_level}
                        onChange={(e) => setFormData({ ...formData, seniority_level: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">1 = highest seniority</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PIN Code (4 digits)
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength="4"
                      value={formData.pin_code}
                      onChange={(e) => setFormData({...formData, pin_code: e.target.value.replace(/\D/g, '')})}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="1234"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Employees use this PIN to log into the Employee Portal
                    </p>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {editingEmployee ? 'Save Changes' : 'Add Employee'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Employee List */}
          <div className="lg:col-span-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">All Employees</h2>
            
            {/* Search Bar */}
<div className="bg-white rounded-lg shadow p-4 mb-2">
  <input
    type="text"
    placeholder="Search employees..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    className="w-full px-3 py-2 focus:outline-none"
    style={{ border: 'none', background: 'transparent' }}
  />
</div>
            
            <div className="space-y-2">
              {employees
                .filter(e => e.active)
                .filter(e => {
                  const fullName = `${e.first_name} ${e.last_name}`.toLowerCase();
                  return fullName.includes(searchTerm.toLowerCase());
                })
                .map((employee) => (
                <div
                  key={employee.id}
                  onClick={() => fetchEmployeeDetails(employee.id)}
                  className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-all ${
                    selectedEmployee?.id === employee.id ? 'ring-2 ring-blue-500' : 'hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {employee.first_name} {employee.last_name}
                      </h3>
                      <p className="text-sm text-gray-500">ID: {employee.id} • Seniority: {employee.seniority_level}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(employee); }}
                        className="text-blue-500 hover:text-blue-700 text-sm"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Employee Details */}
          <div className="lg:col-span-2">
            {selectedEmployee ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {selectedEmployee.first_name} {selectedEmployee.last_name}
                    </h2>
                    <p className="text-gray-500">Employee ID: {selectedEmployee.id} • Seniority Level: {selectedEmployee.seniority_level}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(selectedEmployee.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Deactivate
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-gray-900">{selectedEmployee.phone || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900">{selectedEmployee.email || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Base Pay Rate</p>
                    <p className="text-gray-900">${parseFloat(selectedEmployee.base_pay_rate || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">PIN Code</p>
                    <p className="text-gray-900 font-mono text-lg">{selectedEmployee.pin_code || 'Not set'}</p>
                  </div>
                </div>

                {/* Eligible Locations */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Eligible Locations</h3>
                    <button
                      onClick={() => setShowLocationForm(true)}
                      className="text-blue-500 hover:text-blue-700 text-sm"
                    >
                      + Add Location
                    </button>
                  </div>

                  {showLocationForm && (
                    <form onSubmit={handleAddLocation} className="bg-gray-50 rounded p-4 mb-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                          <select
                            value={locationFormData.location_id}
                            onChange={(e) => setLocationFormData({ ...locationFormData, location_id: e.target.value })}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                            required
                          >
                            <option value="">Select location</option>
                            {locations.filter(l => l.active).map((loc) => (
                              <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Template Rank</label>
                          <input
                            type="number"
                            min="1"
                            value={locationFormData.template_rank}
                            onChange={(e) => setLocationFormData({ ...locationFormData, template_rank: e.target.value })}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                            placeholder="1 = first pick"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 mt-4">
                        <button
                          type="button"
                          onClick={() => setShowLocationForm(false)}
                          className="px-3 py-1 text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Add
                        </button>
                      </div>
                    </form>
                  )}

                  {selectedEmployee.eligible_locations?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedEmployee.eligible_locations.map((loc) => (
                        <div key={loc.id} className="flex justify-between items-center bg-gray-50 rounded p-3">
                          <div>
                            <span className="font-medium">{loc.name}</span>
                            <span className="text-sm text-gray-500 ml-2">Rank: {loc.template_rank}</span>
                          </div>
                          <button
                            onClick={() => handleRemoveLocation(loc.id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No locations assigned yet.</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-gray-500">Select an employee to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Employees;